import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { fetchHiringFeedApiUrl } from "@src/appV2/redesign/HiringFeed/api/usePaginatedHiringFeed";
import {
  type DiscoveryPlacementsResponse,
  fetchDiscoveryPlacementsApiUrl,
} from "@src/appV2/redesign/Placements/api/useFetchDiscoveryPlacements";
import { fetchPlacementsApiUrl } from "@src/appV2/redesign/Placements/api/useFetchPaginatedPlacements";
import { fetchPlacementApplicationsApiUrl } from "@src/appV2/redesign/Placements/api/useFetchPlacementApplications";
import type { GetPlacementsResponse } from "@src/appV2/redesign/Placements/types/fetchPlacements.schema";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

/**
 * Invalidates the placement list caches that change when an application is created or
 * cancelled: the legacy hiring feed, v1 placements, v2 discovery placements, and the
 * worker's placement-applications list.
 *
 * Uses `refetchType: 'all'` so inactive queries are refetched too — without this, a list
 * that was unmounted (e.g. user navigated to the placement details page) would only be
 * marked stale and the user could see the pre-action state when navigating back.
 */
export async function invalidatePlacementListCaches(queryClient: QueryClient, workerId: string) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [fetchHiringFeedApiUrl(workerId)],
      refetchType: "all",
    }),
    queryClient.invalidateQueries({
      queryKey: [fetchPlacementsApiUrl(workerId)],
      refetchType: "all",
    }),
    queryClient.invalidateQueries({
      queryKey: [fetchDiscoveryPlacementsApiUrl(workerId)],
      refetchType: "all",
    }),
    queryClient.invalidateQueries({
      queryKey: [fetchPlacementApplicationsApiUrl(workerId)],
      refetchType: "all",
    }),
  ]);
}

/**
 * Cancels any in-flight list fetches so a pending response can't overwrite a subsequent
 * synchronous cache update (e.g. `removePlacementFromPaginatedListCaches`) with stale
 * server data that still includes the just-applied placement.
 */
export async function cancelPlacementListQueries(queryClient: QueryClient, workerId: string) {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: [fetchHiringFeedApiUrl(workerId)] }),
    queryClient.cancelQueries({ queryKey: [fetchPlacementsApiUrl(workerId)] }),
    queryClient.cancelQueries({ queryKey: [fetchDiscoveryPlacementsApiUrl(workerId)] }),
  ]);
}

/**
 * Removes a placement from all paginated list caches (hiring feed, placements, discovery
 * placements). Use after a worker applies/books an interview so the placement disappears
 * from the discovery list immediately rather than waiting for a stale-refetch to complete.
 */
export function removePlacementFromPaginatedListCaches(
  queryClient: QueryClient,
  workerId: string,
  placementId: string
) {
  queryClient.setQueriesData<InfiniteData<HiringFeedsResponse>>(
    [fetchHiringFeedApiUrl(workerId)],
    (oldData) => removePlacementFromPaginatedQuery(oldData, placementId)
  );

  queryClient.setQueriesData<InfiniteData<GetPlacementsResponse>>(
    [fetchPlacementsApiUrl(workerId)],
    (oldData) => removePlacementFromPaginatedQuery(oldData, placementId)
  );

  queryClient.setQueriesData<InfiniteData<DiscoveryPlacementsResponse>>(
    [fetchDiscoveryPlacementsApiUrl(workerId)],
    (oldData) => removePlacementFromPaginatedQuery(oldData, placementId)
  );
}

function removePlacementFromPaginatedQuery<T extends { data: Array<{ id: string }> }>(
  oldData: InfiniteData<T> | undefined,
  placementId: string
): InfiniteData<T> | undefined {
  if (!oldData) {
    return oldData;
  }

  const newPages = oldData.pages.map((page) => {
    if (!page) {
      return page;
    }

    return {
      ...page,
      data: page.data.filter((item) => item.id !== placementId),
    };
  });

  return { ...oldData, pages: newPages };
}
